export const ProjectStatus = Object.freeze({
  IN_PROGRESS: 'IN_PROGRESS',

  IN_QUEUE: 'IN_QUEUE',

  ABORTED: 'ABORTED',

  FINISHED: 'FINISHED',

  USER_INTERACTION_NEEDED: 'USER_INTERACTION_NEEDED',

  NO_RESULTS: 'NO_RESULTS',

  ERROR_RETRY: 'ERROR_RETRY',

  DELETED: 'DELETED',

  FATAL_ERROR: 'FATAL_ERROR',

  VALIDATION_ERROR: 'VALIDATION_ERROR',

  CLEANED: 'CLEANED',

  NEEDS_PROCESSING: 'NEEDS_PROCESSING',

  get allTypes() {
    return [
      ProjectStatus.IN_PROGRESS,
      ProjectStatus.IN_QUEUE,
      ProjectStatus.ABORTED,
      ProjectStatus.FINISHED,
      ProjectStatus.USER_INTERACTION_NEEDED,
      ProjectStatus.NO_RESULTS,
      ProjectStatus.ERROR_RETRY,
      ProjectStatus.DELETED,
      ProjectStatus.FATAL_ERROR,
      ProjectStatus.VALIDATION_ERROR,
      ProjectStatus.CLEANED,
      ProjectStatus.NEEDS_PROCESSING,
    ];
  },
});

export const RouteType = Object.freeze({
  PUBLISHED: 'PUBLISHED',

  PREDICTED: 'RETROSYNTHESIS',

  CUSTOMIZED: 'CUSTOMIZED',

  COMBINED: 'COMBINED',

  get allTypes() {
    return [RouteType.PUBLISHED, RouteType.PREDICTED, RouteType.CUSTOMIZED, RouteType.COMBINED];
  },

  get trackingMap() {
    return {
      [RouteType.PUBLISHED]: 'published',
      [RouteType.PREDICTED]: 'predicted',
      [RouteType.CUSTOMIZED]: 'customized',
      [RouteType.COMBINED]: 'combined',
    };
  },
});

export const UrlParams = Object.freeze({
  LIMIT_TO: 'limitTo',

  PAGE: 'page',

  SIZE: 'size',

  SORT: 'sort',

  IDS: 'ids',

  STEREOSPECIFIC: 'stereospecific',
});

export const PaginationResponseParams = Object.freeze({
  PAGE_NUMBER: 'number',

  SIZE: 'size',

  TOTAL_PAGES: 'totalPages',

  TOTAL_ELEMENTS: 'totalElements',

  SORT: 'sort',
});

export const SortDirection = Object.freeze({
  ASC: 'ASC',

  DESC: 'DESC',

  get allTypes() {
    return [SortDirection.ASC, SortDirection.DESC];
  },
});

// Structure images path from BE response
export const PATH_ROUTE_SVG = 'ROUTE_SVG'; // SVG - for Tree View
export const PATH_PLANNER_S = 'PLANNER_S'; // PNG - for Route Overview Tree Popup and Table View
export const PATH_PLANNER_SCALED_S = 'PLANNER_SCALED_S'; // For Routes overview
export const PATH_PLANNER_SCALED_S_2STEPS = 'PLANNER_SCALED_S_2STEPS'; // For routes overview with only 1 or 2 steps

export const PublishedParams = Object.freeze({
  FULL_SYNTHESIS_PLAN: 'fullSynthesisPlan',

  NUMBER_OF_PLANS: 'numberOfPlans',

  MAX_BRANCH: 'maxBranch',

  MAX_DEPTH: 'maxDepth',

  STOP_COMMAND: 'stopCommand',

  DEFAULT_YIELD: 'defaultYield',

  get allTypes() {
    return [
      PublishedParams.FULL_SYNTHESIS_PLAN,
      PublishedParams.MAX_DEPTH,
      PublishedParams.MAX_BRANCH,
      PublishedParams.STOP_COMMAND,
      PublishedParams.DEFAULT_YIELD,
    ];
  },
});

export const PredictedPaiParams = Object.freeze({
  FULL_SYNTHESIS_PLAN: 'fullSynthesisPlan',

  NUMBER_OF_PLANS: 'numberOfPlans',

  IDENTICAL_REACTIONS_STEPS: 'identicalReactionsSteps',

  IDENTICAL_BUILDING_BLOCKS: 'identicalBuildingBlocks',

  PROCESSING_TIME: 'processingTime',

  USED_BUILDING_BLOCKS: 'usedBuildingBlocks',

  INCLUDE_RPS: 'includeRPS',

  get allTypes() {
    return [
      PredictedPaiParams.FULL_SYNTHESIS_PLAN,
      PredictedPaiParams.IDENTICAL_REACTIONS_STEPS,
      PredictedPaiParams.IDENTICAL_BUILDING_BLOCKS,
      PredictedPaiParams.PROCESSING_TIME,
      PredictedPaiParams.USED_BUILDING_BLOCKS,
      PredictedPaiParams.INCLUDE_RPS,
    ];
  },
});

export const PredictedIktosParams = Object.freeze({
  MAX_PRICE_PER_GRAM: 'maxPricePerGram',

  SHIPPING_TIME: 'shippingTime',

  MAX_NO_OF_STEPS: 'maxNoOfSteps',

  INCLUDE_SUBSTRUCTURES: 'includeSubstructures',

  EXCLUDE_SUBSTRUCTURES: 'excludeSubstructures',

  SELECTIVITY: 'selectivity',

  STEREO: 'poolChiral',

  USED_BUILDING_BLOCKS: 'usedBuildingBlocks',

  INCLUDE_RPS: 'includeRPS',

  PROCESSING_TIME: 'processingTime',

  get allTypes() {
    return [
      PredictedIktosParams.MAX_NO_OF_STEPS,
      PredictedIktosParams.STEREO,
      PredictedIktosParams.SELECTIVITY,
      PredictedIktosParams.SHIPPING_TIME,
      PredictedIktosParams.MAX_PRICE_PER_GRAM,
      PredictedIktosParams.USED_BUILDING_BLOCKS,
      PredictedIktosParams.INCLUDE_RPS,
      PredictedIktosParams.PROCESSING_TIME,
      PredictedIktosParams.INCLUDE_SUBSTRUCTURES,
      PredictedIktosParams.EXCLUDE_SUBSTRUCTURES,
    ];
  },
});

export const PREDICTED_IKTOS_PROCESSING_TIME_INTERVALS = [
  {
    label: 'Standard',
    value: 10,
  },
  {
    label: 'Extended',
    value: 30,
  },
];

export const PREDICTED_PROCESSING_TIME_INTERVALS = [
  {
    label: 'Standard',
    value: 15,
  },
  {
    label: 'Extended',
    value: 30,
  },
];

export const ProjectSynthesisJobType = Object.freeze({
  PUBLISHED: 'published',

  PREDICTED: 'predicted',

  CUSTOMIZED: 'customized', // Customized doesn't really have a job, put it here for convenience purpose

  COMBINED: 'combined',

  get allTypes() {
    return [
      ProjectSynthesisJobType.PUBLISHED,
      ProjectSynthesisJobType.PREDICTED,
      ProjectSynthesisJobType.CUSTOMIZED,
      ProjectSynthesisJobType.COMBINED,
    ];
  },
});

export const ProjectSynthesisPartner = Object.freeze({
  PAI: 'PAI',

  IKTOS: 'IKTOS',

  get allPartners() {
    return [ProjectSynthesisPartner.PAI, ProjectSynthesisPartner.IKTOS];
  },
});

export const STRUCTURE_DETAILS_FIRSTDISCONNECT = 'First Disconnect';
export const STRUCTURE_DETAILS_RCSANY = 'RCS: delivery time any';
export const STRUCTURE_DETAILS_STEREOIGNORED = 'Stereochemistry ignored';
export const STRUCTURE_DETAILS_LASTSTEPONLY = 'Last step only';
export const STRUCTURE_DETAILS_EXTENDED_TIME = 'Extended processing time';
export const STRUCTURE_DETAILS_ALL_BB = 'Building blocks libraries All';
export const STRUCTURE_DETAILS_10_STEPS = '10 identical reaction steps';
export const STRUCTURE_DETAILS_10_BLOCKS = '10 identical building blocks';

export function getChildStructureDetails(partner, step) {
  if (partner === ProjectSynthesisPartner.PAI) {
    switch (step) {
      case 1:
        return [STRUCTURE_DETAILS_10_STEPS, STRUCTURE_DETAILS_10_BLOCKS, STRUCTURE_DETAILS_EXTENDED_TIME];
      case 2:
        return [STRUCTURE_DETAILS_LASTSTEPONLY, STRUCTURE_DETAILS_10_STEPS, STRUCTURE_DETAILS_10_BLOCKS, STRUCTURE_DETAILS_EXTENDED_TIME];
      default:
        return [];
    }
  } else if (partner === ProjectSynthesisPartner.IKTOS) {
    switch (step) {
      case 1:
        return [STRUCTURE_DETAILS_RCSANY, STRUCTURE_DETAILS_STEREOIGNORED, STRUCTURE_DETAILS_EXTENDED_TIME];
      case 2:
        return [
          STRUCTURE_DETAILS_FIRSTDISCONNECT,
          STRUCTURE_DETAILS_RCSANY,
          STRUCTURE_DETAILS_STEREOIGNORED,
          STRUCTURE_DETAILS_EXTENDED_TIME,
        ];
      default:
        return [];
    }
  }
  return [];
}

export function getHighlightedChildParameters(partner, step) {
  if (partner === ProjectSynthesisPartner.PAI) {
    switch (step) {
      case 1:
        return [
          PredictedPaiParams.IDENTICAL_REACTIONS_STEPS,
          PredictedPaiParams.IDENTICAL_BUILDING_BLOCKS,
          PredictedPaiParams.PROCESSING_TIME,
        ];
      case 2:
        return [
          PredictedPaiParams.FULL_SYNTHESIS_PLAN,
          PredictedPaiParams.IDENTICAL_REACTIONS_STEPS,
          PredictedPaiParams.IDENTICAL_BUILDING_BLOCKS,
          PredictedPaiParams.PROCESSING_TIME,
        ];
      default:
        return [];
    }
  } else if (partner === ProjectSynthesisPartner.IKTOS) {
    switch (step) {
      case 1:
        return [PredictedIktosParams.STEREO, PredictedIktosParams.SHIPPING_TIME, PredictedIktosParams.PROCESSING_TIME];
      case 2:
        return [
          PredictedIktosParams.MAX_NO_OF_STEPS,
          PredictedIktosParams.STEREO,
          PredictedIktosParams.SHIPPING_TIME,
          PredictedIktosParams.PROCESSING_TIME,
        ];
      default:
        return [];
    }
  }
  return [];
}

// unique step identifier for AA
export function getRouteStepIdForAA(step) {
  const substanceId = step && step.substance && (step.substance.rxn || step.substance.inChiKey);
  return step && `${substanceId}_${step.step}`;
}

export function getProjectType(project) {
  if (project.parentId && project.predictedParameters) {
    const params = typeof project.predictedParameters === 'string' ? JSON.parse(project.predictedParameters) : project.predictedParameters;
    if (
      (params.partner === ProjectSynthesisPartner.PAI && !params.fullSynthesisPlan) ||
      (params.partner === ProjectSynthesisPartner.IKTOS && params.maxNoOfSteps === 1)
    ) {
      return 'grouped:automatic-last_step_only';
    }
    return 'grouped:automatic-extended';
  }
  return 'single:user-created';
}

export function getProjectRoutesTypeFromInfoMapForAA(projectInfo) {
  const type = getProjectType(projectInfo);
  return `${type}:project:predicted(${projectInfo.predictedRoutesCount || 0}):published(${projectInfo.publishedRoutesCount || 0}):custom(${
    projectInfo.customizedRoutesCount || 0
  })`;
}

export function getProjectRoutesTypeForAA(project) {
  const type = getProjectType(project);
  return `${type}:project:predicted(${project.rtRouteCount || 0}):published(${project.publishedRouteCount || 0}):custom(${
    project.customizedRouteCount || 0
  })`;
}

export function getProjectDetail(project) {
  return `project:${project.parentId ? project.parentId : project.id}`;
}
